import { Injectable, inject } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { InteractionRequiredAuthError, type AccountInfo } from "@azure/msal-browser";

import { AuthService } from "../services/auth.service";
import { environment } from "../../../environments/environment";

import type { IAuthProvider } from "./interfaces";

const GRAPH_PHOTO_URL = "https://graph.microsoft.com/v1.0/me/photo/$value";
const AVATAR_CACHE_PREFIX = "sp_avatar:";

/**
 * Azure AD implementation of IAuthProvider for the internal Service Provider
 * instance. Silently acquires the SP-API-audience token from the shared tenant
 * session; falls back to an interactive redirect. A sessionStorage flag guards
 * against redirect loops.
 */
@Injectable({ providedIn: "root" })
export class SpAuthService implements IAuthProvider {
  private static readonly REDIRECT_FLAG = "sp_sso_redirecting";

  private readonly msal = inject(MsalService);

  private readonly authState = inject(AuthService);

  async initialize(loginHint: string | null): Promise<void> {
    await this.msal.instance.initialize();

    // navigateToLoginRequestUrl: false keeps the browser on the redirect URI
    // ("/") after an interactive redirect rather than bouncing back to the page
    // that triggered sign-in (often "/login"). The auth guard forwards "/" to
    // the app home once authenticated; the default (true) would strand the user
    // on /login after a successful login. In MSAL v5 this is a per-call option.
    const redirectResult = await this.msal.instance.handleRedirectPromise({
      navigateToLoginRequestUrl: false,
    });
    if (redirectResult?.account) {
      this.msal.instance.setActiveAccount(redirectResult.account);
    }

    let account = this.msal.instance.getActiveAccount();

    // When handleRedirectPromise resolves null (e.g. MSAL's default
    // navigateToLoginRequestUrl bounce, or a returning user with a cached
    // session), the account still lives in the token cache but is not yet
    // active. Promote it before resorting to an interactive flow — otherwise a
    // hint-less ssoSilent always throws and we redirect in an endless loop.
    if (!account) {
      const cached = this.msal.instance.getAllAccounts();
      if (cached.length > 0) {
        account = cached[0] ?? null;
        if (account) {
          this.msal.instance.setActiveAccount(account);
        }
      }
    }

    if (!account) {
      // ssoSilent runs in a hidden iframe and only resolves when it has a
      // login_hint to match (or a session cookie the iframe is allowed to
      // send). With no hint and no cached account there is nothing to resolve:
      // browsers withhold the AAD session cookie from the iframe (third-party
      // cookie blocking), so AAD returns login_required — but because the
      // redirect URI ("/") serves this SPA rather than a blank page, MSAL never
      // reads that response and instead waits out its full iframe timeout
      // (~10s of blank screen) before failing. Skip the doomed silent call and
      // go straight to the interactive redirect, which is a top-level
      // navigation where the session cookie IS sent.
      if (!loginHint) {
        this.redirectToLogin(loginHint);
        return; // navigating away
      }
      try {
        const result = await this.msal.instance.ssoSilent({
          scopes: environment.scopes.loginRequest,
          loginHint,
        });
        account = result.account;
        this.msal.instance.setActiveAccount(account);
      } catch {
        this.redirectToLogin(loginHint);
        return; // navigating away
      }
    }

    if (account) {
      // Authenticated — clear the loop guard so a future legitimate redirect
      // is not blocked, then publish user state.
      sessionStorage.removeItem(SpAuthService.REDIRECT_FLAG);

      // Fetch the API access token BEFORE publishing. The user's roles exist
      // only in that token (the SPA registration defines no app roles), and
      // `buildAuthUser` treats a role-less principal as unauthenticated in
      // production. Publishing first and refreshing later would leave a window
      // where the route guard sees no roles and shows "Access Denied".
      //
      // Silent-only on purpose: this runs on every app start-up, and the
      // redirecting variant would turn a missing consent into a navigation
      // loop here. A null token degrades to id-token roles, which is correct
      // for the Auth0 provider and no worse than the previous behaviour.
      const apiAccessToken = await this.acquireApiTokenSilent();

      this.authState.setFromAccount(account, apiAccessToken);
      void this.loadAadPhoto(account);
    }
  }

  /**
   * Access token for the SP API, silent attempts only — never redirects.
   *
   * Separate from `acquireApiToken` because that one escalates to
   * `acquireTokenRedirect`, which is right for an interceptor reacting to a
   * live 401 but wrong during start-up, where it would navigate away mid-init.
   */
  private async acquireApiTokenSilent(): Promise<string | null> {
    const account = this.msal.instance.getActiveAccount();
    if (!account) {
      return null;
    }
    try {
      const result = await this.msal.instance.acquireTokenSilent({
        scopes: environment.apiConfig.scopes,
        account,
      });
      return result.accessToken;
    } catch {
      return null; // no consent / expired — fall back to id-token roles
    }
  }

  async acquireApiToken(): Promise<string | null> {
    const account = this.msal.instance.getActiveAccount();
    if (!account) {
      return null;
    }
    try {
      const result = await this.msal.instance.acquireTokenSilent({
        scopes: environment.apiConfig.scopes,
        account,
      });
      return result.accessToken;
    } catch (error: unknown) {
      if (error instanceof InteractionRequiredAuthError) {
        await this.msal.instance.acquireTokenRedirect({
          scopes: environment.apiConfig.scopes,
          account,
        });
      }
      return null;
    }
  }

  async login(loginHint: string | null): Promise<void> {
    await this.msal.instance.initialize();
    // Explicit user action — bypass the automatic one-shot loop guard so the
    // manual "Sign in" button always works, even if a prior auto-redirect set
    // the flag and returned without an account (escape hatch).
    sessionStorage.removeItem(SpAuthService.REDIRECT_FLAG);
    this.redirectToLogin(loginHint);
  }

  async logout(): Promise<void> {
    this.authState.clearToken();
    SpAuthService.clearAvatarCache();
    await this.msal.instance.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/login`,
    });
  }

  /** Drop any cached AAD profile photos from sessionStorage on sign-out. */
  private static clearAvatarCache(): void {
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
        const key: string | null = sessionStorage.key(i);
        if (key !== null && key.startsWith(AVATAR_CACHE_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch {
      // sessionStorage unavailable — nothing to clear.
    }
  }

  /**
   * Best-effort: fetch the AAD profile photo from MS Graph and publish it as
   * a data URL. Hydrates from a sessionStorage cache first. Never throws.
   */
  private async loadAadPhoto(account: AccountInfo): Promise<void> {
    const cacheKey = `${AVATAR_CACHE_PREFIX}${account.localAccountId}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        this.authState.setAvatarUrl(cached);
        return;
      }
    } catch {
      // sessionStorage unavailable — ignore and fetch fresh.
    }

    try {
      const result = await this.msal.instance.acquireTokenSilent({
        scopes: environment.scopes.graph,
        account,
      });
      const response = await fetch(GRAPH_PHOTO_URL, {
        headers: { authorization: `Bearer ${result.accessToken}` },
      });
      if (!response.ok) {
        return; // 404 = no photo; keep initials.
      }
      const blob = await response.blob();
      const dataUrl = await SpAuthService.blobToDataUrl(blob);
      this.authState.setAvatarUrl(dataUrl);
      try {
        sessionStorage.setItem(cacheKey, dataUrl);
      } catch {
        // cache write failed — non-fatal.
      }
    } catch {
      // token/fetch/parse failure — keep whatever we have (cached or null).
    }
  }

  private static async blobToDataUrl(blob: Blob): Promise<string> {
    const buf = await blob.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = "";
    for (const b of bytes) {
      binary += String.fromCharCode(b);
    }
    return `data:${blob.type};base64,${btoa(binary)}`;
  }

  /** Interactive redirect with a one-shot loop guard. */
  private redirectToLogin(loginHint: string | null): void {
    if (sessionStorage.getItem(SpAuthService.REDIRECT_FLAG)) {
      return; // already attempted once this session — avoid an infinite loop
    }
    sessionStorage.setItem(SpAuthService.REDIRECT_FLAG, "1");
    void this.msal.instance.loginRedirect({
      scopes: environment.apiConfig.scopes,
      loginHint: loginHint ?? undefined,
    });
  }
}
